import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Mail, CheckCircle } from "lucide-react";
import { useState } from "react";

export default function BookPreOrder() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
    }
  };

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-px w-8 bg-primary" />
                  <span className="text-primary font-medium tracking-wide uppercase text-sm">Coming Soon</span>
                </div>
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-6 leading-tight" data-testid="text-book-title">
                  Your Guide to Becoming a Startup Founder
                </h1>
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  A comprehensive, no-nonsense guide for aspiring founders — from identifying a problem worth solving to building a product, finding customers, raising capital, and scaling your company.
                </p>

                <div className="space-y-4 mb-10" role="list" aria-label="Book highlights">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                    <p role="listitem" className="text-muted-foreground">Actionable frameworks for validating your idea and finding product-market fit</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                    <p role="listitem" className="text-muted-foreground">Real-world strategies for fundraising, pitching investors, and managing your cap table</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                    <p role="listitem" className="text-muted-foreground">Lessons from the trenches on hiring, leadership, and navigating the early-stage chaos</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                    <p role="listitem" className="text-muted-foreground">Written by Matt Rebeiro — strategic advisor to founders and CEOs</p>
                  </div>
                </div>

                {submitted ? (
                  <div className="bg-primary/10 border border-primary/20 rounded-xl p-6" role="status" data-testid="text-book-confirmation">
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle size={20} className="text-primary" aria-hidden="true" />
                      <h3 className="font-display font-semibold text-lg">You're on the list!</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      We'll notify you at <strong className="text-foreground">{email}</strong> as soon as pre-orders open. Thank you for your interest.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Label htmlFor="book-email" className="text-base font-semibold flex items-center gap-2">
                      <Mail size={16} className="text-primary" aria-hidden="true" />
                      Get notified when pre-orders open
                    </Label>
                    <div className="flex gap-3">
                      <Input
                        id="book-email"
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="flex-1"
                        data-testid="input-book-email"
                      />
                      <Button type="submit" className="font-semibold" data-testid="button-book-notify">
                        <BookOpen size={16} className="mr-2" aria-hidden="true" />
                        Notify Me
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">No spam. You'll only receive updates about the book launch.</p>
                  </form>
                )}
              </div>

              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-2xl" aria-hidden="true" />
                  <img
                    src="/book-cover.png"
                    alt="Your Guide to Becoming a Startup Founder — Book Cover"
                    className="relative rounded-2xl shadow-2xl border border-white/10 max-w-sm w-full"
                    data-testid="img-book-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
